import { HttpResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { toPairs } from "lodash";
import { ServerEventsService } from "src/app/services/server-events.service";
import { SseEvents } from "src/app/sse/sse-events";
import { SseCartItemDeletedPayload } from "src/app/sse/payloads/sse-cart-item-deleted.payload";
import { SseEventPayloads } from "src/app/sse/sse-payload";
import { SseCartItemAddedPayload } from "src/app/sse/payloads/sse-cart-item-added.payload";
import { SseCartItemUpdatedPayload } from "src/app/sse/payloads/sse-cart-item-updated.payload";
import { appState } from "src/app/core/state";

type SntCacheItem = {
  timestamp: Date;
  value: HttpResponse<any>;
  lastUpdate?: Date;
};

type CacheEntitiesIdsExtractorFn = (messageEvent: SseEventPayloads) => string[];

const CACHE_ENTITIES_ID_MAP: Partial<{
  [key in SseEvents]: CacheEntitiesIdsExtractorFn;
}> = {
  [SseEvents.CART_ITEM_ADDED]: (payload: SseCartItemAddedPayload): string[] => {
    return [payload.cartId];
  },
  [SseEvents.CART_ITEM_UPDATED]: (
    payload: SseCartItemUpdatedPayload
  ): string[] => {
    return [payload.cartId];
  },
  [SseEvents.CART_ITEM_DELETED]: (
    payload: SseCartItemDeletedPayload
  ): string[] => {
    return [payload.cartId];
  }
};

@Injectable()
export class CacheService {
  static instance: CacheService;

  private cacheEnabled = true;

  private cache: Map<string, SntCacheItem> = new Map<string, SntCacheItem>();

  private serverEventsService = inject(ServerEventsService);

  constructor() {
    CacheService.instance = this;

    toPairs(SseEvents).forEach(([key, value]) => {
      this.serverEventsService.addListener(
        value,
        (message) => {
          try {
            const data = JSON.parse((<any>message).data);
            const cacheKey = this.getKey(data.id);

            const relatedIdsFactory =
              CACHE_ENTITIES_ID_MAP[<SseEvents>message.type];
            if (!!relatedIdsFactory) {
              const idsToDelete = relatedIdsFactory(data);

              idsToDelete.forEach((id) => {
                this.cache.delete(id);
              });
            }

            this.cache.delete(cacheKey);

            for (const key of this.cache.keys()) {
              if (key.startsWith(cacheKey)) {
                this.cache.delete(key);
              }
            }
          } catch (ex) {
            console.error(ex);
          }
        },
        true
      );
    });

    appState.cache = this.cache;
  }

  isEnabled(): boolean {
    return this.cacheEnabled;
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  get(key: string): SntCacheItem | undefined {
    return this.cache.get(key);
  }

  set(key: string, item: SntCacheItem): void {
    this.cache.set(key, item);
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  getKey(id: string, params?: Object): string {
    return `${id}-${params?.toString() || ""}`;
  }
}
